import React, { useMemo } from "react";

import classNames from "classnames";
import { Col, Row, Pagination as BSPagination } from "react-bootstrap";
import { usePaginationContext } from "../contexts/Pagination";

interface Props {
    useContext: () => {
        totalItems: number;
    };
}

const Pagination = React.memo((props: Props) => {
    const { useContext } = props;
    const { totalItems } = useContext();

    const { itemsPerPage, page, setPage } = usePaginationContext();
    const paginate = (pageNumber: number) => setPage(pageNumber);

    const showing = useMemo(() => {
        const min = (page - 1) * itemsPerPage + 1;
        const max =
            page * itemsPerPage > totalItems
                ? totalItems
                : totalItems < itemsPerPage
                  ? totalItems
                  : page * itemsPerPage;
        return { min, max };
    }, [page, itemsPerPage, totalItems]);

    const pageCount = useMemo(() => {
        return Math.ceil(totalItems / itemsPerPage);
    }, [itemsPerPage, totalItems]);

    return (
        <Row className={classNames({ pagination: true })}>
            <Col xs={3}>
                <p>
                    Showing {showing.min} to {showing.max} of {totalItems}{" "}
                    entries
                </p>
            </Col>
            <Col>
                <BSPagination>
                    {page !== 1 && (
                        <>
                            <BSPagination.First
                                onClick={() => paginate(1)}
                                aria-label="First Page"
                                title="First Page"
                            />

                            <BSPagination.Prev
                                onClick={() => paginate(page - 1)}
                                aria-label="Previous Page"
                                title="Previous Page"
                            />
                        </>
                    )}

                    {pageCount > 5 ? (
                        <>
                            {page >= 3 && (
                                <BSPagination.Item
                                    onClick={() => paginate(page - 2)}
                                    aria-label={`Page ${page - 2}`}
                                    title={`Page ${page - 2}`}
                                >
                                    {page - 2}
                                </BSPagination.Item>
                            )}

                            {page >= 2 && (
                                <BSPagination.Item
                                    onClick={() => paginate(page - 1)}
                                    aria-label={`Page ${page - 1}`}
                                    title={`Page ${page - 1}`}
                                >
                                    {page - 1}
                                </BSPagination.Item>
                            )}

                            <BSPagination.Item
                                active
                                aria-label={`Page ${page}`}
                                title={`Page ${page}`}
                            >
                                {page}
                            </BSPagination.Item>

                            {page <= pageCount - 1 && (
                                <BSPagination.Item
                                    onClick={() => paginate(page + 1)}
                                    aria-label={`Page ${page + 1}`}
                                    title={`Page ${page + 1}`}
                                >
                                    {page + 1}
                                </BSPagination.Item>
                            )}

                            {page <= pageCount - 2 && (
                                <BSPagination.Item
                                    onClick={() => paginate(page + 2)}
                                    aria-label={`Page ${page + 2}`}
                                    title={`Page ${page + 2}`}
                                >
                                    {page + 2}
                                </BSPagination.Item>
                            )}
                        </>
                    ) : (
                        [...Array(pageCount)].map((_, index) => (
                            <BSPagination.Item
                                key={index}
                                active={page === index + 1}
                                onClick={() => paginate(index + 1)}
                                aria-label={`Page ${index + 1}`}
                                title={`Page ${index + 1}`}
                            >
                                {index + 1}
                            </BSPagination.Item>
                        ))
                    )}
                    {page !== pageCount && (
                        <>
                            <BSPagination.Next
                                onClick={() => paginate(page + 1)}
                                aria-label={`Next Page`}
                                title={`Next Page`}
                            />

                            <BSPagination.Last
                                onClick={() => paginate(pageCount)}
                                aria-label={`Last Page}`}
                                title={`Last Page}`}
                            />
                        </>
                    )}
                </BSPagination>
            </Col>
        </Row>
    );
});

export default Pagination;
